import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { projectsAPI, projectFoldersAPI, usersAPI } from '../services/api';
import { Project, ProjectFolder, User } from '../types';
import { useAuth } from '../contexts/AuthContext';

type FilterType = 'urgent' | 'regular' | 'completed' | 'all';

const Projects: React.FC = () => {
  const { folderSlug } = useParams<{ folderSlug?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [folders, setFolders] = useState<ProjectFolder[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [currentFolder, setCurrentFolder] = useState<ProjectFolder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('urgent');
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    folder: null as string | null,
    team: [] as string[],
    startDate: null as Date | null,
    endDate: null as Date | null,
    urgent: false
  });
  const [descriptionDialogVisible, setDescriptionDialogVisible] = useState(false);
  const [selectedProjectDescription, setSelectedProjectDescription] = useState<{ name: string; description: string } | null>(null);
  const [teamDialogVisible, setTeamDialogVisible] = useState(false);
  const [selectedProjectTeam, setSelectedProjectTeam] = useState<{ name: string; team: Project['team'] } | null>(null);
  const [folderDialogVisible, setFolderDialogVisible] = useState(false);
  const [selectedProjectForFolder, setSelectedProjectForFolder] = useState<Project | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [teamPanelOpen, setTeamPanelOpen] = useState(false);
  const [teamSearch, setTeamSearch] = useState('');

  // Učitaj poslednji izabrani prikaz (Hitni / Redovni / Završeni / Svi) iz localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('projectsLastFilter');
      if (stored === 'urgent' || stored === 'regular' || stored === 'completed' || stored === 'all') {
        setFilterType(stored);
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    loadFolders();
    loadUsers();
  }, []);

  // Ponovo učitaj korisnike kad korisnik otvori dijalog (npr. ako je prvi load pao ili još nije stigao)
  useEffect(() => {
    if (dialogVisible && users.length === 0) {
      loadUsers();
    }
  }, [dialogVisible]);

  useEffect(() => {
    if (folderSlug && folders.length > 0) {
      const folder = folders.find(f => f.slug === folderSlug);
      if (folder) {
        setCurrentFolder(folder);
      } else {
        setError('Folder nije pronađen');
      }
    } else if (!folderSlug) {
      setCurrentFolder(null);
    }
  }, [folderSlug, folders]);

  const loadFolders = async () => {
    try {
      const data = await projectFoldersAPI.getAll();
      setFolders(data.sort((a, b) => a.order - b.order));
    } catch (err: any) {
      console.error('Error loading folders:', err);
    }
  };

  const loadUsers = async () => {
    try {
      const data = await usersAPI.getAll();
      setUsers(data);
    } catch (err: any) {
      console.error('Error loading users:', err);
    }
  };

  const loadProjects = async (folderId?: string) => {
    setLoading(true);
    setError('');
    try {
      const params: any = {};
      if (folderId) {
        params.folderId = folderId;
      }
      
      // Primeni filter - UVIJEK primenjuj filter
      if (filterType === 'urgent') {
        // Hitni: samo hitni aktivni projekti
        params.urgent = 'urgent';
        params.status = 'active';
      } else if (filterType === 'regular') {
        // Redovni: samo redovni aktivni projekti
        params.urgent = 'not-urgent';
        params.status = 'active';
      } else if (filterType === 'completed') {
        // Završeni: svi završeni projekti (hitni i redovni)
        params.status = 'completed';
      }
      
      const data = await projectsAPI.getAll(params);
      const projectsList = Array.isArray(data) ? data : (data as any).items || [];
      setProjects(projectsList);
    } catch (err: any) {
      setError(err.message || 'Greška pri učitavanju projekata');
    } finally {
      setLoading(false);
    }
  };

  // Učitaj projekte kada se promeni filter ili folder.
  useEffect(() => {
    // Sačekaj da se folder učita ako postoji folderSlug
    if (folderSlug && folders.length === 0) {
      return; // Sačekaj da se folder učita
    }
    loadProjects(currentFolder?._id);
  }, [filterType, currentFolder?._id, folderSlug, folders.length]);

  const filteredProjects = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return projects;
    return projects.filter((p) => {
      const name = (p.name || '').toLowerCase();
      const desc = (p.description || '').toLowerCase();
      return name.includes(term) || desc.includes(term);
    });
  }, [projects, searchTerm]);

  const handleCreate = () => {
    setEditingProject(null);
    setFormData({
      name: '',
      description: '',
      folder: currentFolder?._id || null,
      team: user?._id ? [user._id] : [],
      startDate: null,
      endDate: null,
      urgent: false
    });
    setError('');
    setTeamPanelOpen(false);
    setTeamSearch('');
    setDialogVisible(true);
  };

  const handleEdit = (project: Project) => {
    setEditingProject(project);
    const folderMapping = project.folderMappings?.find(m => m.userId === user?._id);
    setFormData({
      name: project.name,
      description: project.description || '',
      folder: folderMapping?.folderId || null,
      team: project.team.map(m => m._id),
      startDate: project.startDate ? new Date(project.startDate) : null,
      endDate: project.endDate ? new Date(project.endDate) : null,
      urgent: project.urgent
    });
    setError('');
    setTeamPanelOpen(false);
    setTeamSearch('');
    setDialogVisible(true);
  };

  const handleDelete = (project: Project) => {
    confirmDialog({
      message: `Da li ste sigurni da želite da obrišete projekat "${project.name}"?`,
      header: 'Potvrda brisanja',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        try {
          await projectsAPI.delete(project._id);
          await loadProjects(currentFolder?._id);
        } catch (err: any) {
          setError(err.message || 'Greška pri brisanju projekta');
        }
      }
    });
  };

  const handleComplete = async (project: Project) => {
    try {
      await projectsAPI.updateStatus(project._id, 'completed');
      await loadProjects(currentFolder?._id);
    } catch (err: any) {
      setError(err.message || 'Greška pri označavanju projekta');
    }
  };

  const handleChangeFolder = (project: Project) => {
    setSelectedProjectForFolder(project);
    const folderMapping = project.folderMappings?.find(m => m.userId === user?._id);
    setSelectedFolderId(folderMapping?.folderId || null);
    setFolderDialogVisible(true);
  };

  const handleSaveFolderChange = async () => {
    if (!selectedProjectForFolder) return;
    
    try {
      const updatedProject = await projectsAPI.update(selectedProjectForFolder._id, {
        folder: selectedFolderId
      });
      setFolderDialogVisible(false);
      setSelectedProjectForFolder(null);
      setSelectedFolderId(null);
      // Reload folders first, then projects to ensure colors update
      await loadFolders();
      // Reload projects to get updated folderMappings
      await loadProjects(currentFolder?._id);
    } catch (err: any) {
      setError(err.message || 'Greška pri promeni foldera');
    }
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError('Naziv projekta je obavezan');
      return;
    }
    if (formData.team.length === 0) {
      setError('Morate izabrati bar jednog člana tima');
      return;
    }

    try {
      if (editingProject) {
        await projectsAPI.update(editingProject._id, {
          name: formData.name,
          description: formData.description,
          folder: formData.folder,
          team: formData.team,
          startDate: formData.startDate?.toISOString(),
          endDate: formData.endDate?.toISOString(),
          urgent: formData.urgent
        });
        setDialogVisible(false);
      } else {
        await projectsAPI.create({
          name: formData.name,
          description: formData.description,
          folder: formData.folder || undefined,
          team: formData.team,
          startDate: formData.startDate?.toISOString(),
          endDate: formData.endDate?.toISOString(),
          urgent: formData.urgent
        });
        // Reset form and close dialog after creating project
        setFormData({
          name: '',
          description: '',
          folder: currentFolder?._id || null,
          team: [],
          startDate: null,
          endDate: null,
          urgent: false
        });
        setError('');
        setDialogVisible(false);
      }
      await loadProjects(currentFolder?._id);
    } catch (err: any) {
      setError(err.message || 'Greška pri čuvanju projekta');
    }
  };

  const statusBodyTemplate = (rowData: Project) => {
    const statusMap: Record<string, { label: string; severity: 'success' | 'info' | 'warning' | 'danger' }> = {
      active: { label: 'Aktivan', severity: 'success' },
      completed: { label: 'Završen', severity: 'info' },
      on_hold: { label: 'Na čekanju', severity: 'warning' },
      cancelled: { label: 'Otkazan', severity: 'danger' }
    };
    const status = statusMap[rowData.status] || { label: rowData.status, severity: 'info' };
    return <Tag value={status.label} severity={status.severity} />;
  };

  const nameBodyTemplate = (rowData: Project) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {rowData.urgent && <Tag value="HITNO" severity="danger" />}
        <span style={{ cursor: 'pointer' }} onClick={() => navigate(`/projects/${rowData._id}`)}>
          {rowData.name}
        </span>
      </div>
    );
  };

  const descriptionBodyTemplate = (rowData: Project) => {
    if (rowData.description) {
      return (
        <i 
          className="pi pi-info-circle" 
          style={{ color: '#6b7280', cursor: 'pointer', fontSize: '1.2rem' }}
          onClick={(e) => {
            e.stopPropagation();
            setSelectedProjectDescription({
              name: rowData.name,
              description: rowData.description || ''
            });
            setDescriptionDialogVisible(true);
          }}
          title="Kliknite za prikaz opisa"
        />
      );
    }
    return '-';
  };

  const teamBodyTemplate = (rowData: Project) => {
    if (!rowData.team || rowData.team.length === 0) return '-';
    const handleShowAll = () => {
      setSelectedProjectTeam({
        name: rowData.name,
        team: rowData.team
      });
      setTeamDialogVisible(true);
    };

    return (
      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
        {rowData.team.slice(0, 3).map((member) => (
          member.avatarUrl ? (
            <img
              key={member._id}
              src={member.avatarUrl}
              alt={`${member.firstName} ${member.lastName}`}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                objectFit: 'cover',
                border: '2px solid #e5e7eb'
              }}
              title={`${member.firstName} ${member.lastName}`}
              onError={(e) => {
                // Fallback na inicijale ako slika ne može da se učita
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = document.createElement('div');
                fallback.style.cssText = 'width: 32px; height: 32px; border-radius: 50%; background-color: #9ca3af; color: white; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: bold;';
                fallback.textContent = `${member.firstName[0]}${member.lastName[0]}`;
                fallback.title = `${member.firstName} ${member.lastName}`;
                target.parentNode?.appendChild(fallback);
              }}
            />
          ) : (
            <div
              key={member._id}
              style={{
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#9ca3af',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 'bold'
              }}
              title={`${member.firstName} ${member.lastName}`}
            >
              {member.firstName[0]}{member.lastName[0]}
            </div>
          )
        ))}
        {rowData.team.length > 3 && (
          <div
            style={{ fontSize: '0.875rem', color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}
            onClick={(e) => {
              e.stopPropagation();
              handleShowAll();
            }}
            title="Prikaži sve članove tima"
          >
            +{rowData.team.length - 3}
          </div>
        )}
      </div>
    );
  };

  const creatorBodyTemplate = (rowData: Project) => {
    const hasAvatar = rowData.createdBy?.avatarUrl && rowData.createdBy.avatarUrl.trim() !== '';
    
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {hasAvatar ? (
          <img
            src={rowData.createdBy.avatarUrl}
            alt={`${rowData.createdBy.firstName} ${rowData.createdBy.lastName}`}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              objectFit: 'cover',
              border: '2px solid #e5e7eb'
            }}
            title={`${rowData.createdBy.firstName} ${rowData.createdBy.lastName}`}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const fallback = document.createElement('div');
              fallback.style.cssText = 'width: 32px; height: 32px; border-radius: 50%; background-color: #6366f1; color: white; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: bold;';
              fallback.textContent = `${rowData.createdBy.firstName[0]}${rowData.createdBy.lastName[0]}`;
              target.parentNode?.appendChild(fallback);
            }}
          />
        ) : (
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              backgroundColor: '#6366f1',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.75rem',
              fontWeight: 'bold'
            }}
            title={`${rowData.createdBy.firstName} ${rowData.createdBy.lastName}`}
          >
            {rowData.createdBy.firstName[0]}{rowData.createdBy.lastName[0]}
          </div>
        )}
      </div>
    );
  };

  const canEditProject = (project: Project) => {
    if (!user) return false;
    // Admin i direktor mogu da menjaju sve projekte
    if (user.role === 'admin' || user.role === 'direktor') {
      return true;
    }
    // Kreator projekta može da menja svoj projekat
    return project.createdBy && project.createdBy._id === user._id;
  };

  const actionsBodyTemplate = (rowData: Project) => {
    const canEdit = canEditProject(rowData);
    
    return (
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        {canEdit && rowData.status !== 'completed' && (
          <i
            className="pi pi-check"
            style={{ 
              color: '#10b981', 
              cursor: 'pointer', 
              fontSize: '1rem',
              transition: 'opacity 0.2s'
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleComplete(rowData);
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
            onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
            title="Označi kao završen"
          />
        )}
        {canEdit && (
          <>
            <i
              className="pi pi-folder"
              style={{ 
                color: (() => {
                  const folderMapping = rowData.folderMappings?.find(m => m.userId === user?._id);
                  const projectFolder = folderMapping ? folders.find(f => f._id === folderMapping.folderId) : null;
                  return projectFolder ? projectFolder.color : '#6b7280';
                })(),
                cursor: 'pointer', 
                fontSize: '1rem',
                transition: 'opacity 0.2s'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleChangeFolder(rowData);
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              title="Promeni folder"
            />
            <i
              className="pi pi-pencil"
              style={{ 
                color: '#6b7280', 
                cursor: 'pointer', 
                fontSize: '1rem',
                transition: 'opacity 0.2s'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(rowData);
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              title="Izmeni"
            />
            <i
              className="pi pi-trash"
              style={{ 
                color: '#6b7280', 
                cursor: 'pointer', 
                fontSize: '1rem',
                transition: 'opacity 0.2s'
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(rowData);
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '0.7'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
              title="Obriši"
            />
          </>
        )}
        {!canEdit && (
          <span style={{ color: '#9ca3af', fontSize: '0.75rem' }}>—</span>
        )}
      </div>
    );
  };

  const folderOptions = [
    { label: 'Bez foldera', value: null },
    ...folders.map(f => ({ label: f.name, value: f._id }))
  ];

  const userOptions = users.map(u => ({
    label: `${u.firstName} ${u.lastName}`,
    value: u._id
  }));

  const selectedTeamMembers = users.filter(u => formData.team.includes(u._id));
  const teamDisplayValue =
    selectedTeamMembers.length === 0
      ? 'Izaberite članove tima'
      : selectedTeamMembers.map(u => `${u.firstName} ${u.lastName}`).join(', ');

  const toggleTeamMember = (userId: string) => {
    setFormData(prev => {
      const exists = prev.team.includes(userId);
      const team = exists
        ? prev.team.filter(id => id !== userId)
        : [...prev.team, userId];
      return { ...prev, team };
    });
  };

  return (
    <div style={{ padding: '2rem' }}>
      <ConfirmDialog />
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h1>
            {currentFolder ? (
              <>
                <i className={currentFolder.icon} style={{ color: currentFolder.color, marginRight: '0.5rem' }} />
                {currentFolder.name}
              </>
            ) : (
              'Upravljanje projektima'
            )}
          </h1>
          <Button
            label="                                                                                                                                                             Dodaj projekat"
            icon="pi pi-plus"
            className="p-button-success"
            onClick={handleCreate}
          />
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
            <span style={{ fontWeight: 'bold' }}>Prikaz:</span>
            <Button
              label="Hitni"
              className={filterType === 'urgent' ? 'p-button-primary' : 'p-button-outlined'}
            onClick={() => {
              setFilterType('urgent');
              try {
                localStorage.setItem('projectsLastFilter', 'urgent');
              } catch {
                // ignore
              }
            }}
              size="small"
            />
            <Button
              label="Redovni"
              className={filterType === 'regular' ? 'p-button-primary' : 'p-button-outlined'}
            onClick={() => {
              setFilterType('regular');
              try {
                localStorage.setItem('projectsLastFilter', 'regular');
              } catch {
                // ignore
              }
            }}
              size="small"
            />
            <Button
              label="Završeni"
              className={filterType === 'completed' ? 'p-button-primary' : 'p-button-outlined'}
            onClick={() => {
              setFilterType('completed');
              try {
                localStorage.setItem('projectsLastFilter', 'completed');
              } catch {
                // ignore
              }
            }}
              size="small"
            />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginLeft: 'auto' }}>
            <span style={{ fontWeight: 'bold' }}>Pretraga:</span>
            <span className="p-input-icon-left" style={{ width: '300px' }}>
              <i className="pi pi-search" style={{ left: '0.75rem' }} />
              <InputText
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Pretraži projekat..."
                style={{ width: '100%', paddingLeft: '2.5rem' }}
              />
            </span>
          </div>
        </div>
      </div>

      {error && (
        <Message severity="error" text={error} style={{ marginBottom: '1rem' }} />
      )}

      <DataTable
        value={filteredProjects}
        loading={loading}
        paginator
        rows={10}
        emptyMessage="Nema projekata"
        key={`projects-${folders.map(f => f._id).join('-')}-${filteredProjects.map(p => p._id).join('-')}`}
        onRowClick={(e) => navigate(`/projects/${e.data._id}`)}
        className="cursor-pointer"
        responsiveLayout="stack"
        breakpoint="768px"
      >
        <Column field="name" header="Naziv" body={nameBodyTemplate} sortable />
        <Column field="description" header="Opis" body={descriptionBodyTemplate} />
        <Column field="team" header="Tim" body={teamBodyTemplate} />
        <Column field="status" header="Status" body={statusBodyTemplate} sortable />
        <Column
          field="startDate"
          header="Početak"
          body={(rowData: Project) => rowData.startDate ? new Date(rowData.startDate).toLocaleDateString('sr-RS') : '-'}
          sortable
        />
        <Column
          field="endDate"
          header="Kraj"
          body={(rowData: Project) => rowData.endDate ? new Date(rowData.endDate).toLocaleDateString('sr-RS') : '-'}
          sortable
        />
        <Column field="createdBy" header="Kreator" body={creatorBodyTemplate} />
        <Column body={actionsBodyTemplate} header="Akcije" style={{ width: '200px' }} />
      </DataTable>

      <Dialog
        header={editingProject ? 'Izmeni projekat' : 'Dodaj projekat'}
        visible={dialogVisible}
        style={{ width: '600px' }}
        contentStyle={{ maxHeight: '80vh', overflow: 'visible' }}
        onHide={() => {
          setDialogVisible(false);
          setTeamPanelOpen(false);
          setTeamSearch('');
        }}
        footer={
          <div>
            <Button label="Otkaži" icon="pi pi-times" onClick={() => {
              setDialogVisible(false);
              setTeamPanelOpen(false);
              setTeamSearch('');
            }} className="p-button-text" />
            <Button label={editingProject ? 'Sačuvaj' : 'Kreiraj'} icon="pi pi-check" onClick={handleSubmit} />
          </div>
        }
      >
        {error && <Message severity="error" text={error} style={{ marginBottom: '1rem' }} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '70vh', overflow: 'auto' }}>
          <div>
            <label htmlFor="name" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Naziv projekta <span style={{ color: 'red' }}>*</span>
            </label>
            <InputText
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label htmlFor="description" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Opis
            </label>
            <InputTextarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              style={{ width: '100%' }}
              rows={3}
            />
          </div>
          <div>
            <label htmlFor="folder" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Folder (opciono)
            </label>
            <Dropdown
              id="folder"
              value={formData.folder}
              options={folderOptions}
              onChange={(e) => setFormData({ ...formData, folder: e.value })}
              appendTo="self"
              style={{ width: '100%' }}
              placeholder="Bez foldera"
              showClear
            />
            <small style={{ color: '#666' }}>Organizujte projekat u folder za lakšu navigaciju</small>
          </div>
          <div>
            <label htmlFor="team" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Članovi tima <span style={{ color: 'red' }}>*</span>
            </label>
            <div style={{ position: 'relative' }}>
              <div
                id="team"
                onClick={() => {
                  const nextOpen = !teamPanelOpen;
                  setTeamPanelOpen(nextOpen);
                  if (nextOpen && users.length === 0) loadUsers();
                }}
                style={{
                  width: '100%',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  padding: '0.5rem 0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  cursor: 'pointer',
                  backgroundColor: 'white'
                }}
              >
                <span
                  style={{
                    color: selectedTeamMembers.length === 0 ? '#9ca3af' : '#111827',
                    fontSize: '0.9rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    marginRight: '0.5rem'
                  }}
                >
                  {teamDisplayValue}
                </span>
                <i
                  className={`pi pi-chevron-${teamPanelOpen ? 'up' : 'down'}`}
                  style={{ fontSize: '0.9rem', color: '#6b7280' }}
                />
              </div>
              {teamPanelOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    marginTop: '0.25rem',
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                    maxHeight: '260px',
                    overflow: 'hidden',
                    zIndex: 1000,
                    boxShadow:
                      '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)'
                  }}
                >
                  <div
                    style={{
                      padding: '0.4rem 0.75rem',
                      borderBottom: '1px solid #e5e7eb',
                      backgroundColor: '#f9fafb'
                    }}
                  >
                    <InputText
                      value={teamSearch}
                      onChange={(e) => setTeamSearch(e.target.value)}
                      placeholder="Pretraži po imenu..."
                      style={{ width: '100%', fontSize: '0.85rem' }}
                    />
                  </div>
                  <div style={{ maxHeight: '210px', overflowY: 'auto' }}>
                    {users.length === 0 && (
                      <div
                        style={{
                          padding: '0.5rem 0.75rem',
                          fontSize: '0.85rem',
                          color: '#6b7280'
                        }}
                      >
                        Nema dostupnih korisnika.
                      </div>
                    )}
                    {/* Select all */}
                    {users.length > 0 && (
                      <label
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.4rem 0.75rem',
                          cursor: 'pointer',
                          fontSize: '0.9rem',
                          borderBottom: '1px solid #f3f4f6',
                          fontWeight: 500
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            formData.team.length > 0 &&
                            formData.team.length === users.length
                          }
                          onChange={() => {
                            setFormData(prev => {
                              const allIds = users.map(u => u._id);
                              const allSelected =
                                prev.team.length === allIds.length &&
                                allIds.every(id => prev.team.includes(id));
                              return {
                                ...prev,
                                team: allSelected ? [] : allIds
                              };
                            });
                          }}
                          style={{ margin: 0 }}
                        />
                        <span>Selektuj sve</span>
                      </label>
                    )}
                    {users
                      .filter(u => {
                        const term = teamSearch.trim().toLowerCase();
                        if (!term) return true;
                        return (
                          u.firstName.toLowerCase().includes(term) ||
                          u.lastName.toLowerCase().includes(term) ||
                          u.username.toLowerCase().includes(term)
                        );
                      })
                      .map(user => {
                        const checked = formData.team.includes(user._id);
                        return (
                          <label
                            key={user._id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              padding: '0.4rem 0.75rem',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              borderBottom: '1px solid #f3f4f6'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleTeamMember(user._id)}
                              style={{ margin: 0 }}
                            />
                            <span>
                              {user.firstName} {user.lastName}
                            </span>
                          </label>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label htmlFor="startDate" style={{ display: 'block', marginBottom: '0.5rem' }}>
                Datum početka
              </label>
              <Calendar
                id="startDate"
                value={formData.startDate}
                onChange={(e) => setFormData({ ...formData, startDate: e.value as Date | null })}
                appendTo="self"
                panelClassName="compact-calendar"
                panelStyle={{ width: '100%' }}
                style={{ width: '100%' }}
                showIcon
                dateFormat="dd.mm.yy"
              />
            </div>
            <div>
              <label htmlFor="endDate" style={{ display: 'block', marginBottom: '0.5rem' }}>
                Datum završetka
              </label>
              <Calendar
                id="endDate"
                value={formData.endDate}
                onChange={(e) => setFormData({ ...formData, endDate: e.value as Date | null })}
                appendTo="self"
                panelClassName="compact-calendar"
                panelStyle={{ width: '100%' }}
                style={{ width: '100%' }}
                showIcon
                dateFormat="dd.mm.yy"
              />
            </div>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Checkbox
                inputId="urgent"
                checked={formData.urgent}
                onChange={(e) => setFormData({ ...formData, urgent: e.checked || false })}
              />
              <label htmlFor="urgent" style={{ cursor: 'pointer' }}>
                Hitno (projekat će biti istaknut crvenom bojom)
              </label>
            </div>
          </div>
        </div>
      </Dialog>

      <Dialog
        header={selectedProjectDescription?.name || 'Opis projekta'}
        visible={descriptionDialogVisible}
        style={{ width: '600px' }}
        onHide={() => {
          setDescriptionDialogVisible(false);
          setSelectedProjectDescription(null);
        }}
        footer={
          <Button label="Zatvori" icon="pi pi-times" onClick={() => {
            setDescriptionDialogVisible(false);
            setSelectedProjectDescription(null);
          }} />
        }
      >
        <div style={{ padding: '1rem 0' }}>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
            {selectedProjectDescription?.description || ''}
          </p>
        </div>
      </Dialog>

      <Dialog
        header={selectedProjectTeam?.name ? `Tim projekta: ${selectedProjectTeam.name}` : 'Tim projekta'}
        visible={teamDialogVisible}
        style={{ width: '450px' }}
        onHide={() => {
          setTeamDialogVisible(false);
          setSelectedProjectTeam(null);
        }}
        footer={
          <Button
            label="Zatvori"
            icon="pi pi-times"
            onClick={() => {
              setTeamDialogVisible(false);
              setSelectedProjectTeam(null);
            }}
          />
        }
      >
        <div style={{ padding: '1rem 0', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {selectedProjectTeam?.team?.map((member) => (
            <div
              key={member._id}
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}
            >
              {member.avatarUrl ? (
                <img
                  src={member.avatarUrl}
                  alt={`${member.firstName} ${member.lastName}`}
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    border: '2px solid #e5e7eb'
                  }}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: '#9ca3af',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}
                >
                  {member.firstName[0]}{member.lastName[0]}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontWeight: 500 }}>
                  {member.firstName} {member.lastName}
                </span>
                <span style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  {member.username}
                </span>
              </div>
            </div>
          ))}
          {(!selectedProjectTeam || selectedProjectTeam.team.length === 0) && (
            <p style={{ margin: 0 }}>Nema članova tima za ovaj projekat.</p>
          )}
        </div>
      </Dialog>

      <Dialog
        header={selectedProjectForFolder ? `Promeni folder za: ${selectedProjectForFolder.name}` : 'Promeni folder'}
        visible={folderDialogVisible}
        style={{ width: '600px', height: '100%' }}
        onHide={() => {
          setFolderDialogVisible(false);
          setSelectedProjectForFolder(null);
          setSelectedFolderId(null);
        }}
        footer={
          <div>
            <Button
              label="Otkaži"
              icon="pi pi-times"
              onClick={() => {
                setFolderDialogVisible(false);
                setSelectedProjectForFolder(null);
                setSelectedFolderId(null);
              }}
              className="p-button-text"
            />
            <Button
              label="Sačuvaj"
              icon="pi pi-check"
              onClick={handleSaveFolderChange}
            />
          </div>
        }
      >
        <div style={{ padding: '1rem 0' }}>
          <label htmlFor="folderSelect" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Folder
          </label>
          <Dropdown
            id="folderSelect"
            value={selectedFolderId}
            options={folderOptions}
            onChange={(e) => setSelectedFolderId(e.value)}
            appendTo="self"
            style={{ width: '100%' }}
            placeholder="Bez foldera"
            showClear
            filter
            filterPlaceholder="Pretraži foldere..."
            scrollHeight="200px"
          />
          <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
            Izaberite folder u koji želite da premestite projekat
          </small>
        </div>
      </Dialog>
    </div>
  );
};

export default Projects;
